import {
  annualHealthcareInflationRate,
  formatCurrency,
  CareEnvironment,
  FundingPolicyType,
} from '@shared';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import 'katex/dist/katex.min.css';
import Latex from 'react-latex-next';
import { useSelector } from 'react-redux';
import { selectClient } from '@/model/selectors';

interface clientFirstNameProps {
  clientFirstName: string;
}

export function ResultsAreInSlideOutContent(props: clientFirstNameProps) {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4 ">
        <div>
          <h1 className="flex flex-grow flex-col gap-3 pb-2 text-lg font-semibold md:text-2xl">
            Data and methodology
          </h1>
          <p className="pb-3 text-sm md:text-base">
            These 50,000 families have been reporting data over the last 20 years. We know what they
            looked like before needing LTC, and how their care needs progressed over time.
          </p>
          <p className="text-sm md:text-base">
            By looking at people whose historical responses are similar to {props.clientFirstName},
            the AI uses their eventual LTC outcomes to make predictions about{' '}
            {props.clientFirstName}&apos;s future LTC.{' '}
          </p>
        </div>
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">More information</h2>
          <p className="text-sm md:text-base">
            For more information on Waterlily predictive models, how they work and what they can and
            can't do, please visit our{' '}
            <a
              href={'https://www.waterlily.com/planning-for-yourself/disclosures-disclaimers'}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              disclaimers and methodology page
            </a>
            .
          </p>
        </div>
      </div>
    </>
  );
}

export function LtcFundingSlideOutContent() {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4 ">
        <div>
          <h1 className="flex flex-grow flex-col gap-3 pb-2 text-xl font-semibold">
            Projected Cost Coverage
          </h1>
          <p className="pb-3 text-sm md:text-base">
            The estimated amount that you will have available to cover long-term care (LTC) costs
            based on your current financing plan. This sum encompasses both self-funding
            contributions and policy coverage where applicable.
          </p>
        </div>
        <div>
          <h2 className="pb-2 text-xl font-semibold">Self-Funding Coverage</h2>
          <p className="text-sm md:text-base">
            Self-funding coverage may consist of a one-time contribution and/or monthly
            contributions you've chosen to make, along with the growth of these amounts based on
            your selected annual rate of return. The total profit of the investment is then reduced
            by the provided capital gains tax rate to arrive at the net future value of self-funding
            assets. This may not be applicable to those who have already begun long-term care.
          </p>
        </div>
        <div>
          <h2 className="pb-2 text-xl font-semibold">Policy Coverage</h2>
          <p className="text-sm md:text-base">
            If applicable, policy coverage estimates how much of your projected long-term care costs
            will be offset by your policy, considering the specific conditions outlined in your
            long-term care policy and your current care plan. Please note that policies typically do
            not activate until you require assistance with at least two activities of daily living
            (ADLs), and therefore, they do not cover the first half of the Early Care stage.
          </p>
        </div>
        <div>
          <h2 className="pb-2 text-xl font-semibold">Annuity Coverage</h2>
          <p className="text-sm md:text-base">
            Annuity coverage estimates how much of your projected long-term care costs will be
            covered by your annuity payouts. This calculation uses the expected annuity payouts
            starting at your estimated payout age and continues until the end of the long-term care
            period. If there is an enhanced LTC amount, it will be used instead of the regular
            payout during the period when the annuitant meets the Activities of Daily Living (ADL)
            requirements and the payment period has not elapsed.
          </p>
        </div>
      </div>
    </>
  );
}

function FundingSourceDisclaimer() {
  return (
    <div className="text-sm italic text-gray-700">
      <p>
        This information is for educational purposes and is an oversimplification of terms, options,
        and strategies, and does not cover all options in the scenarios described. This is not
        financial advice. For a more detailed and nuanced explanation of the different types of
        funding mechanisms that extends beyond the high level overview provided here, please speak
        with a financial advisor or insurance agent who is licensed in this field.
      </p>
    </div>
  );
}

export function FundingSourcesInsuranceSlideOutContent() {
  return (
    <>
      <div className="-mt-6 flex flex-grow flex-col gap-6 ">
        <div>
          <h1 className="flex flex-grow flex-col gap-3 pb-2 text-xl font-semibold">
            What is insurance for long-term care?
          </h1>
          <p className="pb-2 text-sm md:text-base">
            Long-term care insurance products are designed to cover costs that are typically not
            covered by standard health insurance, such as help with daily activities and extended
            nursing care. Each type of policy has distinct features and benefits to consider.
          </p>
        </div>
        <div>
          <h1 className="flex flex-grow flex-col gap-3 pb-2 text-xl font-semibold">
            What are some pros and cons to buying insurance for long-term care?
          </h1>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Extended Coverage: </span>
            Insurance can cover extensive costs that standard health insurance does not, ensuring
            financial support for services that are often necessary as one ages.
          </p>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Tax Advantages: </span>
            Premiums paid for qualified long-term care insurance can be tax-deductible, depending on
            the policy and your tax situation. This can reduce the overall cost of insurance.
          </p>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Low Initial Capital Requirement: </span>
            Unlike self-funding, which may require significant assets, buying insurance allows you
            to access substantial long-term care coverage without needing a large amount of capital
            upfront. You pay premiums over time, spreading out the cost.
          </p>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Qualification Challenges: </span>
            Not everyone qualifies for long-term care insurance, especially traditional policies.
            Health screenings are required, and pre-existing conditions or poor health can
            disqualify you or increase the cost.
          </p>
        </div>
        <div>
          <h1 className="flex flex-grow flex-col gap-3 pb-2 text-xl font-semibold">
            What kinds of long-term care insurance products are there?
          </h1>
          <p className="pb-2 text-sm md:text-base">
            There are several types of long-term care insurance products. Our platform focuses
            mainly on four types:
          </p>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Traditional long-term care insurance: </span>
            Traditional long-term care insurance is designed specifically to cover the costs of
            long-term care services, such as in-home care, assisted living, and nursing home
            expenses. These policies typically require health screenings and can have variable
            premiums that may increase over time. Coverage is activated when the insured cannot
            perform a certain number of Activities of Daily Living (ADLs) (typically two or more).
          </p>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Hybrid insurance: </span>
            Hybrid insurance combines long-term care insurance with life insurance into a single
            policy. This type of policy ensures that if long-term care is not needed, the premiums
            paid will not be lost but will instead contribute towards a life insurance benefit to
            the beneficiaries. Hybrid policies often require a larger initial investment but offer
            fixed premiums and a death benefit.
          </p>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Life insurance with riders: </span>A life insurance
            policy with a long-term care rider allows policyholders to use part of the death benefit
            for long-term care expenses should they need it during their lifetime. This option
            provides flexibility as it serves both as life insurance and a way to fund long-term
            care, reducing the use-it-or-lose-it risk found in traditional policies.
          </p>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Short-term care insurance: </span>
            Short-term care insurance covers the same types of services as traditional long-term
            care insurance, such as home care or nursing care, but for a shorter duration—typically
            about one year or less. This insurance is often easier to qualify for, has lower
            premiums, and may be suitable for those needing temporary care or who cannot qualify for
            traditional long-term care insurance due to health issues or age.
          </p>
        </div>
        <FundingSourceDisclaimer />
      </div>
    </>
  );
}

export function FundingSourcesSelfFundingSlideOutContent() {
  return (
    <>
      <div className="-mt-6 flex flex-grow flex-col gap-4 ">
        <div>
          <h2 className="flex flex-grow flex-col gap-3 pb-2 text-xl font-semibold">
            What does it mean to self-fund long-term care?
          </h2>
          <p className="pb-1 text-sm md:text-base">
            Self-funding long-term care means that you plan to cover part or all of the costs out of
            your own financial resources, which can include savings, investments, income and other
            assets.
          </p>
        </div>
        <div>
          <h2 className="flex flex-grow flex-col gap-3 pb-2 text-xl font-semibold">
            What are some pros and cons of self-funding?
          </h2>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Increased control: </span>
            Self-funding allows you to retain control over your assets and how they are used for
            your long-term care needs, giving you complete control over the type of care you receive
            and where you receive it. With self-funding, you pay only for the care you use, which
            can mean cost savings if you end up needing less care than planned for.
          </p>

          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Impact on inheritance: </span>A significant risk of
            self-funding is the potential to exhaust your financial resources quickly, especially if
            your care needs extend over a long period. This depletion of funds can adversely affect
            your financial stability and the inheritance you intend to leave to your family.
            Therefore, it's important to carefully consider the likelihood and potential costs of
            needing extended care when deciding on self-funding as an option.
          </p>
          <p className="pb-3 text-sm md:text-base">
            <span className="font-semibold">Less tax-advantage: </span>Unlike some insurance
            premiums which might be tax-deductible, out-of-pocket expenses for self-funded care
            typically do not offer tax benefits, which could increase your overall financial burden.
          </p>
        </div>
        <FundingSourceDisclaimer />
      </div>
    </>
  );
}

export function FundingSourcesAnnuitySlideOutContent() {
  return (
    <>
      <div className="-mt-6 flex flex-grow flex-col gap-6 ">
        <div>
          <h1 className="flex flex-grow flex-col gap-3 pb-2 text-xl font-semibold">
            What are annuities and how can they be used for long-term care?
          </h1>
          <p className="pb-3 text-sm md:text-base">
            Annuities are financial products that you purchase from an insurance company, which then
            provide a guaranteed income stream for life or a specified period. To address long-term
            care needs, some annuities can be structured with long-term care riders that increase
            the annual payout if the annuitant requires long-term care. These products are designed
            to offer a steady source of income, which can be useful in managing long-term care
            expenses.
          </p>
        </div>
        <div>
          <h1 className="flex flex-grow flex-col gap-3 pb-2 text-xl font-semibold">
            What are some pros and cons to using annuities for long-term care costs?
          </h1>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Guaranteed Income: </span>
            Annuities can provide a guaranteed income stream, which can be a reliable foundation for
            covering ongoing long-term care costs, mitigating financial instability from varying
            market conditions.
          </p>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Long-Term Care Riders: </span>
            Some annuities offer the option to add long-term care riders, which increase the payout
            when long-term care services are needed. This can help in covering higher care costs
            without depleting other savings or assets.
          </p>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Tax Advantages: </span>
            The income from annuities can be tax-advantaged depending on the type of annuity, which
            can result in lower tax liabilities compared to other forms of income.
          </p>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Lack of Liquidity: </span>
            Once an annuity is purchased, the funds are typically locked in, and accessing them in a
            lump sum can incur high surrender charges. This lack of liquidity can be a disadvantage
            if your financial situation changes unexpectedly.
          </p>
        </div>
        <FundingSourceDisclaimer />
      </div>
    </>
  );
}

export function FundingSourceLtcPayoutSlideOutContent() {
  return (
    <>
      <div className="-mt-6 flex flex-grow flex-col gap-6 ">
        <div>
          <h2 className="flex flex-grow flex-col gap-3 pb-2 text-xl font-semibold">
            What is LTC Payout and Non-LTC Payout?
          </h2>
          <p className="pb-2 text-sm md:text-base">
            The LTC Payout represents the amount of money from a funding source that will go towards
            covering long-term care costs. Conversely, the Non-LTC Payout is the amount of money
            from a funding source that may not be used for long-term care costs but is still paid to
            or belongs to the owner and/or their beneficiaries.
          </p>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Self-funding: </span> the LTC Payout is equivalent to
            the Total Payout of the investment(s). This assumes that self-funding investments can be
            liquidated to cover any portion or all of the care costs. Non-LTC Payout is always zero
            for self-funding, because we assume that there are no restrictions on how funds can be
            used to cover care costs.
          </p>
          <p className="pb-2 text-sm md:text-base">
            <span className="font-semibold">Insurance: </span> the LTC Payout is limited by the
            maximum benefit amount and daily maximums. Additionally, the coverable portion of
            projected costs is reduced by the elimination period and the portion of care costs
            associated with the period when the policyholder does not meet the activities of daily
            living (ADL) requirements to activate the policy.
          </p>
          <p className="pb-1 text-sm md:text-base">
            The Non-LTC Payout includes any remaining benefits after LTC benefits are paid out. This
            can include death benefits or any other benefits not used for long-term care. For life
            insurance with a rider and hybrid policies, we assume any amount not used for LTC is
            paid out as a death benefit.
          </p>
          <p className="pb-1 text-sm md:text-base">
            <span className="font-semibold">Annuity: </span> the LTC Payout is the sum of annual
            payments that occur after the expected payout begin age and continue until the projected
            long-term care end age. In annuities with enhanced long-term care payouts, these are
            applied only to the portion of projected costs after the period when the annuitant meets
            the ADL requirements, similar to insurance policies.
          </p>
        </div>

        <FundingSourceDisclaimer />
      </div>
    </>
  );
}

export function FundingSourceTotalPayoutSlideOutContent() {
  return (
    <>
      <div className="-mt-4 flex flex-grow flex-col gap-4 ">
        <div>
          <h2 className="flex flex-grow flex-col gap-3 pb-2 text-xl font-semibold">
            What is the total payout?
          </h2>
          <p className="pb-1 text-sm md:text-base">
            The Total Payout is the estimated or guaranteed value of a funding source, intended to
            cover long-term care costs or to be paid to its owner and/or their beneficiaries. It is
            calculated as the sum of the long-term care (LTC) Payout and the Non-LTC Payout.
          </p>
        </div>

        <FundingSourceDisclaimer />
      </div>
    </>
  );
}

export function FundingSourceCostSlideOutContent() {
  return (
    <>
      <div className="-mt-6 flex flex-grow flex-col gap-6 ">
        <div>
          <h2 className="flex flex-grow flex-col gap-3 pb-2 text-xl font-semibold">
            What is the cost of a funding source?
          </h2>
          <p className="pb-1 text-sm md:text-base">
            The cost of a funding source is the total principal amount invested in it. For
            self-funding, this includes the sum of any one-time contributions and monthly
            contributions. For insurance, the cost is the sum of any premiums paid. For annuities,
            it is the purchase price.
          </p>
        </div>
        <FundingSourceDisclaimer />
      </div>
    </>
  );
}

export function CareSettingsSlideOutContent() {
  return (
    <>
      <div className="-mt-4 flex flex-grow flex-col gap-4">
        <div>
          <h1 className="flex flex-grow flex-col gap-3 pb-2 text-lg font-semibold md:text-2xl">
            What are care settings?
          </h1>
          <p className="pb-3 text-sm md:text-base">
            A care setting refers to the environment where an individual receives long-term care.
            This could be one's home, a community setting like assisted living or independent
            living, or a facility such as a nursing home. In Waterlily, you have the flexibility to
            choose different care settings that are appropriate to each stage of care.
          </p>
        </div>
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Cost of care settings</h2>
          <p className="text-sm md:text-base">
            Different care settings come with varying associated costs. By using your zip code, we
            pinpoint the precise cost of your selected care setting in your local area.
          </p>
        </div>
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Home care</h2>
          <p className="text-sm md:text-base">
            Home care involves receiving care at home, either from family/friends or paid
            professionals, such as a home health agency. Depending on the extent of care provided by
            unpaid caregivers, costs for this setting can range from $0 (when all care is provided
            by unpaid caregivers) to the maximum (if all care is provided by professional
            caregivers).
          </p>
        </div>

        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Independent Living</h2>
          <p className="text-sm md:text-base">
            Independent living refers to residential communities designed for seniors who are able
            to live independently but desire the convenience of services such as meals,
            housekeeping, and social activities. Residents typically live in private apartments or
            houses within the community.
          </p>
        </div>
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Assisted Living</h2>
          <p className="text-sm md:text-base">
            Assisted living facilities offer a combination of housing, personalized support
            services, and healthcare designed to meet the needs of individuals who require
            assistance with activities of daily living (ADLs) such as bathing, dressing, and
            medication management. Residents typically live in private or semi-private apartments
            and have access to communal areas and activities.
          </p>
        </div>
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Full Care Facility</h2>
          <p className="text-sm md:text-base">
            Full Care facilities provide nursing home level of care (24/7 medical care and
            assistance with daily activities for individuals with complex medical needs). Residents
            receive specialized care from licensed professionals in a supportive environment
            tailored to their requirements.
          </p>
        </div>
      </div>
    </>
  );
}

export function FactorCareSettingsSlideOutContent() {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4 ">
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">How did we calculate this?</h2>
          <p className="text-sm md:text-base">
            This figure is determined by estimating your future care requirements, such as duration
            and level of care needed, as well as the predicted (or selected) care settings.
          </p>
        </div>

        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Understanding care settings</h2>
          <p className="text-sm md:text-base">
            A care setting refers to the environment where an individual receives long-term care.
            This could include their own home, a community setting like assisted living or
            independent living, or a facility such as a nursing home. In Waterlily, you have the
            flexibility to choose different care settings for each stage of care.
          </p>
        </div>

        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Defining care phases</h2>
          <p className="text-sm md:text-base">
            A care phase represents a specific period within an individual's long-term care journey,
            characterized by their level of care needs. In Waterlily, we simplify this journey into
            three distinct phases, each corresponding to a progressive level of care requirement.
          </p>
        </div>

        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Care setting costs</h2>
          <p className="text-sm md:text-base">
            Different care settings come with varying associated costs. By using your zip code, we
            pinpoint the precise cost of your selected care setting in your local area.
          </p>
        </div>
      </div>
    </>
  );
}

export function FactorFamilyCareGivingSlideOutContent() {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4 ">
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">How did we calculate this?</h2>
          <p className="text-sm md:text-base">
            This amount is determined by considering the local cost of home care services in your
            area, as indicated by your zip code, and the anticipated number of care hours to be
            provided by your family.
          </p>
        </div>

        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Understanding family caregiving</h2>
          <p className="text-sm md:text-base">
            Family caregiving involves unpaid physical care provided by family or friends,
            applicable when care is received at home. We subtract the estimated cost of family
            caregiving from the total projected expenses.
          </p>
        </div>
      </div>
    </>
  );
}

export function FactorHealthCareInflationSlideOutContent() {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4 ">
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">How did we calculate this?</h2>
          <p className="text-sm md:text-base">
            We use a national health care inflation rate<sup>1</sup> and compound from the present
            until your care is expected to begin.
          </p>
        </div>

        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">
            Understanding health care inflation
          </h2>
          <p className="text-sm md:text-base">
            Our models predict the cost of your future long-term care in today's dollars, but these
            expenses are expected to rise due to inflation until your care begins. The amount shown
            reflects the projected increase in costs due to inflation.
          </p>
        </div>
        <ol className="flex flex-col border-t border-gray-200 py-6 italic text-gray-700">
          <li className="flex gap-1">
            <p>1. </p>
            <p className="text-sm md:text-base">
              Annual inflation rate of {annualHealthcareInflationRate * 100}%{' '}
              <a
                href="https://www.cms.gov/newsroom/press-releases/cms-office-actuary-releases-2022-2031-national-health-expenditure-projections"
                target="_blank"
                rel="noreferrer"
              >
                (source: cms.gov)
              </a>
            </p>
          </li>
        </ol>
      </div>
    </>
  );
}

export function CareHoursNeededSlideOutContent() {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4 ">
        <div>
          <h1 className="flex-grow pb-2 text-lg font-semibold md:text-2xl">Care hours needed</h1>
          <p className="text-sm md:text-base">
            The total care hours required over the length of one&apos;s long-term care.
          </p>
        </div>
        <div>
          <h2 className="text-lg font-semibold md:text-xl">
            Model accuracy<sup className="font-normal">1</sup>
          </h2>
          <p className="text-sm md:text-base">
            <BaselineMetricText percentIncrease={45} />
          </p>
        </div>
        <div>
          <h2 className="text-lg font-semibold md:text-xl">
            Top factors<sup className="font-normal">2</sup>
          </h2>
          <p className="text-sm md:text-base">
            Memory, age, gender, ADL/IADL baselines, mental health, arthritis, prescription drug
            use, high blood pressure
          </p>
        </div>
      </div>
      {/* footer */}
      <ol className="flex flex-col border-t border-gray-200 py-6 italic text-gray-700">
        <li className="flex gap-1">
          <TopFactorsFootnote footnoteNumber={1} />
        </li>
        <li className="flex gap-1">
          <BaselineMetricFootnote
            industryMetric="87 monthly care hours"
            sourceUrl="https://acl.gov/ltc/basic-needs/who-will-provide-your-care"
            displayUrl="acl.gov"
            footNoteNumber={2}
          />
        </li>
      </ol>
    </>
  );
}

export function FamilyCarePercentageSlideOutContent() {
  return (
    <>
      <div className="-mt-3 flex flex-grow flex-col gap-4 ">
        <div>
          <h2 className="flex-grow pb-2 text-lg font-semibold md:text-xl">Home care hours</h2>
          <p className="text-sm md:text-base">
            Care at home can be administered by either family/friends (unpaid) or professional care
            services (paid).
          </p>
        </div>
        <div className="pb-2">
          <h2 className="flex-grow pb-2 text-lg font-semibold md:text-xl">
            Determining care hour distribution
          </h2>
          <p className="pb-2 text-sm md:text-base">
            We establish the allocation of care hours between paid and unpaid providers by
            considering your preferences indicated in the intake form and leveraging AI technology
            that predicts how your family would step in based on how other similar families
            contributed to the care of their loved ones.
          </p>
          <p className="text-sm md:text-base">
            Your stated preference influences the proportion of care hours assigned to family versus
            professional caregivers. Subsequently, AI algorithms assist in determining the
            allocation of care hours to specific family members (e.g., spouse, children, other
            family).
          </p>
        </div>

        <div>
          <h2 className="text-lg font-semibold md:text-xl">
            Model accuracy<sup className="font-normal">1</sup>
          </h2>
          <p className="text-sm md:text-base">
            <BaselineMetricText percentIncrease={15} />
          </p>
        </div>
        <div>
          <h2 className="text-lg font-semibold md:text-xl">
            Top factors<sup className="font-normal">2</sup>
          </h2>
          <p className="text-sm md:text-base">
            Marital status, children count, household size, siblings count.
          </p>
        </div>
      </div>
      {/* footer */}
      <ol className="flex flex-col border-t border-gray-200 py-6 italic text-gray-700">
        <li className="flex gap-1">
          <TopFactorsFootnote footnoteNumber={1} />
        </li>
        <li className="flex gap-1">
          <BaselineMetricFootnote
            industryMetric="87 monthly care hours"
            sourceUrl="https://acl.gov/ltc/basic-needs/who-will-provide-your-care"
            displayUrl="acl.gov"
            footNoteNumber={2}
          />
        </li>
      </ol>
    </>
  );
}

export function FamilyCareGivingSlideOutContent() {
  return (
    <>
      <div className="-mt-10 flex flex-grow flex-col gap-4 pb-6">
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Care hours and length of care</h2>
          <p className="text-sm md:text-base">
            These figures refer to the care hours and length of care (in months) specific to the
            care stage.
          </p>
        </div>
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Estimating care stages</h2>
          <p className="text-sm md:text-base">
            Waterlily utilizes advanced statistical growth modeling to estimate the progression of
            your care needs over the course of your long-term care. By analyzing the care
            trajectories of similar individuals, we predict how quickly or slowly your needs may
            evolve. This analysis enables us to estimate the duration of each care stage and the
            level of care needed. Below are additional insights about related models.
          </p>
        </div>

        <div>
          <h2 className="flex-grow pb-2 text-lg font-semibold md:text-xl">
            Total care hours needed
          </h2>
          <p>The total care hours required over the length of one&apos;s long-term care.</p>
        </div>
        <div className="ml-2">
          <h2 className="text-lg font-semibold">
            Model accuracy<sup className="font-normal">1</sup>
          </h2>
          <p>
            <BaselineMetricText percentIncrease={45} />
          </p>
        </div>
        <div className="ml-2 pb-4">
          <h2 className="text-lg font-semibold">
            Top factors<sup className="font-normal">2</sup>
          </h2>
          <p>
            Memory, age, gender, ADL/IADL baselines, mental health, arthritis, prescription drug
            use, high blood pressure
          </p>
        </div>
        <div>
          <h2 className="flex-grow pb-2 text-lg font-semibold md:text-xl">
            Long-term care duration
          </h2>
          <p>The length in years of one&apos;s long-term care.</p>
        </div>
        <div className="ml-2">
          <h2 className="text-lg font-semibold">
            Model accuracy<sup className="font-normal">3</sup>
          </h2>
          <p>
            <BaselineMetricText percentIncrease={10} />
          </p>
        </div>
        <div className="ml-2">
          <h2 className="text-lg font-semibold">
            Top factors<sup className="font-normal">1</sup>
          </h2>
          <p>Memory, ADL/IADL baselines, smoking, BMI, assets, gender</p>
        </div>
      </div>

      {/* footer */}
      <ol className="flex flex-col border-t border-gray-200 py-6 italic text-gray-700">
        <li className="flex gap-1">
          <TopFactorsFootnote footnoteNumber={1} />
        </li>
        <li className="flex gap-1">
          <BaselineMetricFootnote
            industryMetric="87 monthly care hours"
            sourceUrl="https://acl.gov/ltc/basic-needs/who-will-provide-your-care"
            displayUrl="acl.gov"
            footNoteNumber={2}
          />
        </li>
        <li className="flex gap-1">
          <BaselineMetricFootnote
            industryMetric="3 years"
            sourceUrl="https://acl.gov/ltc/basic-needs/who-will-provide-your-care"
            displayUrl="acl.gov"
            footNoteNumber={3}
          />
        </li>
      </ol>
    </>
  );
}

export function EarlyCareSlideOutContent() {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4">
        <div>
          <h1 className="flex-grow pb-2 text-lg font-semibold md:text-2xl">
            What is Early Care stage?
          </h1>
          <p>
            This initial stage marks the beginning of long-term care, approximately characterized by
            needing assistance with 1-2 self-care activities (e.g., bathing and dressing).
          </p>
        </div>
        <CarePhasesSlideOutContent />
      </div>
    </>
  );
}

export function ModerateCareSlideOutContent() {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4">
        <div>
          <h1 className="flex-grow pb-2 text-lg font-semibold md:text-2xl">
            What is Moderate Care stage?
          </h1>
          <p>
            Moderate care represents the second phase of long-term care, approximately characterized
            by needing assistance with 3-4 self-care activities such as bathing, dressing,
            toileting, and transferring.
          </p>
        </div>
        <CarePhasesSlideOutContent />
      </div>
    </>
  );
}

export function FullCareSlideOutContent() {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4">
        <div>
          <h1 className="flex-grow pb-2 text-lg font-semibold md:text-2xl">
            What is Full Care stage?
          </h1>
          <p>
            Full care represents the highest level of care need in long-term care, approximately
            characterized by needing assistance with 5-6 self-care activities. Individuals in this
            stage often rely on significant support to maintain their daily routines and overall
            well-being.
          </p>
        </div>
        <CarePhasesSlideOutContent />
      </div>
    </>
  );
}

export function CarePhasesSlideOutContent() {
  return (
    <>
      <div>
        <h2 className="flex-grow pb-2 text-lg font-semibold md:text-xl">Self-care activities</h2>
        <p>
          These are essential daily tasks individuals perform to care for themselves, including
          bathing, dressing, eating, transferrring (e.g., from bed to chair), toileting, and
          continence. These are also called Activities of Daily Living, or ADLs.
        </p>
      </div>
      <div>
        <h2 className="text-lg font-semibold md:text-xl">Understanding stages of care need</h2>
        <p>
          The stages of care need in long-term care are categorized based on the number of self-care
          tasks an individual requires assistance with. Progressing from early to moderate to full
          stages indicates an increasing level of care required throughout the long-term care
          journey.
        </p>
      </div>
      <div>
        <h2 className="text-lg font-semibold md:text-xl">Estimating care stages</h2>
        <p>
          Waterlily utilizes advanced statistical growth modeling to estimate the progression of
          your care needs over the course of your long-term care. By analyzing the care trajectories
          of similar individuals, we predict how quickly or slowly your needs may evolve.
        </p>
      </div>
    </>
  );
}

export function LikelihoodSlideOutContent() {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4 ">
        <div>
          <h1 className="flex-grow pb-2 text-lg font-semibold md:text-2xl">
            Likelihood of long-term care
          </h1>
          <p className="text-sm md:text-base">
            The likelihood of needing long-term care (LTC)<sup>1</sup> during one&apos;s lifetime.
          </p>
        </div>
        <div>
          <h2 className="text-lg font-semibold md:text-xl">Model accuracy: 78%</h2>
          <p className="text-sm md:text-base">
            Accuracy based on an average of the true positive rate and precision (positive
            predictive value) achieved on data the model has not been trained on.
          </p>
        </div>
        <div>
          <h2 className="text-lg font-semibold md:text-xl">
            Top factors<sup className="font-normal">2</sup>
          </h2>
          <p className="text-sm md:text-base">
            Age, memory, ADL/IADL baselines, sex, BMI, prescription drug use, arthritis, diabetes.
          </p>
        </div>
      </div>

      {/* footer */}
      <ol className="flex flex-col border-t border-gray-200 py-6 italic text-gray-700">
        <li className="flex gap-1">
          <LtcDefinitionFootnote footnoteNumber={1} />
        </li>
        <li className="flex gap-1">
          <TopFactorsFootnote footnoteNumber={2} />
        </li>
      </ol>
    </>
  );
}

export function DurationSlideOutContent() {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4 ">
        <div>
          <h1 className="flex-grow pb-2 text-lg font-semibold md:text-2xl">Length of Care</h1>
          <p className="text-sm md:text-base">
            This is an estimate predicting the duration of long-term care (LTC)
            <sup className="font-normal">1</sup> from the onset of care needs to the conclusion of
            those needs.
          </p>
          <p className="pt-1 text-sm md:text-base">
            This prediction is based on a comparative analysis of one's data with that of similar
            individuals who have previously undergone long-term care. It is important to note that
            this estimate does not imply mortality, but rather provides a forecast to help one
            understand and plan for potential long-term care.
          </p>
        </div>
        <div>
          <h2 className="text-lg font-semibold md:text-xl">
            Model accuracy<sup>2</sup>
          </h2>
          <BaselineMetricText percentIncrease={10} />
        </div>
        <div>
          <h2 className="text-lg font-semibold md:text-xl">
            Top factors<sup className="font-normal">3</sup>
          </h2>
          <p className="text-sm md:text-base">
            Memory, ADL/IADL baselines, smoking, BMI, assets, sex.
          </p>
        </div>
      </div>

      {/* footer */}
      <ol className="flex flex-col border-t border-gray-200 py-6 italic text-gray-700">
        <li className="flex gap-1">
          <LtcDefinitionFootnote footnoteNumber={1} />
        </li>
        <li className="flex gap-1">
          <BaselineMetricFootnote
            industryMetric="3 years"
            sourceUrl="https://acl.gov/ltc/basic-needs/who-will-provide-your-care"
            displayUrl="acl.gov"
            footNoteNumber={2}
          />
        </li>
        <li className="flex gap-1">
          <TopFactorsFootnote footnoteNumber={3} />
        </li>
      </ol>
    </>
  );
}

export function CareHoursStartSlideOutContent() {
  return (
    <>
      <div className="-mt-10 flex flex-grow flex-col gap-4 pb-6">
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-2xl">Initial care needs</h2>
          <p className="text-sm md:text-base">
            This estimate represents the average number of caregiving hours you will require at the
            beginning of your long-term care. It covers the support needed to perform basic
            self-care activities such as bathing, dressing, and other daily tasks.
          </p>
        </div>
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Estimating care stages</h2>
          <p className="text-sm md:text-base">
            Waterlily utilizes advanced statistical growth modeling to estimate the progression of
            your care needs over the course of your long-term care. By analyzing the care
            trajectories of similar individuals, we predict how quickly or slowly your needs may
            evolve. This analysis enables us to estimate the duration of each care stage and the
            level of care needed. Below are additional insights about related models.
          </p>
        </div>

        <div>
          <h2 className="flex-grow pb-2 text-lg font-semibold md:text-xl">
            Total care hours needed
          </h2>
          <p className="text-sm md:text-base">
            The total care hours required over the length of one&apos;s long-term care.
          </p>
        </div>
        <div className="ml-2">
          <h2 className="text-lg font-semibold md:text-xl">
            Model accuracy<sup className="font-normal">1</sup>
          </h2>
          <p className="text-sm md:text-base">
            <BaselineMetricText percentIncrease={45} />
          </p>
        </div>
        <div className="ml-2 pb-4">
          <h2 className="text-lg font-semibold md:text-xl">
            Top factors<sup className="font-normal">2</sup>
          </h2>
          <p className="text-sm md:text-base">
            Memory, age, gender, ADL/IADL baselines, mental health, arthritis, prescription drug
            use, high blood pressure
          </p>
        </div>
      </div>

      {/* footer */}
      <ol className="flex flex-col border-t border-gray-200 py-6 italic text-gray-700">
        <li className="flex gap-1">
          <TopFactorsFootnote footnoteNumber={1} />
        </li>
        <li className="flex gap-1">
          <BaselineMetricFootnote
            industryMetric="87 monthly care hours"
            sourceUrl="https://acl.gov/ltc/basic-needs/who-will-provide-your-care"
            displayUrl="acl.gov"
            footNoteNumber={2}
          />
        </li>
      </ol>
    </>
  );
}

export function CareHoursEndSlideOutContent() {
  return (
    <>
      <div className="-mt-10 flex flex-grow flex-col gap-4 pb-6">
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-2xl">Later care needs</h2>
          <p className="text-sm md:text-base">
            This estimate represents the average number of caregiving hours you will need during the
            later stages of your long-term care. It includes support for basic self-care needs and
            more intensive care activities as your needs increase over time.
          </p>
        </div>
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Estimating care stages</h2>
          <p className="text-sm md:text-base">
            Waterlily utilizes advanced statistical growth modeling to estimate the progression of
            your care needs over the course of your long-term care. By analyzing the care
            trajectories of similar individuals, we predict how quickly or slowly your needs may
            evolve. This analysis enables us to estimate the duration of each care stage and the
            level of care needed. Below are additional insights about related models.
          </p>
        </div>

        <div>
          <h2 className="flex-grow pb-2 text-lg font-semibold md:text-xl">
            Total care hours needed
          </h2>
          <p className="text-sm md:text-base">
            The total care hours required over the length of one&apos;s long-term care.
          </p>
        </div>
        <div className="ml-2">
          <h2 className="text-lg font-semibold md:text-xl">
            Model accuracy<sup className="font-normal">1</sup>
          </h2>
          <p className="text-sm md:text-base">
            <BaselineMetricText percentIncrease={45} />
          </p>
        </div>
        <div className="ml-2 pb-4">
          <h2 className="text-lg font-semibold md:text-xl">
            Top factors<sup className="font-normal">2</sup>
          </h2>
          <p className="text-sm md:text-base">
            Memory, age, gender, ADL/IADL baselines, mental health, arthritis, prescription drug
            use, high blood pressure
          </p>
        </div>
      </div>

      {/* footer */}
      <ol className="flex flex-col border-t border-gray-200 py-6 italic text-gray-700">
        <li className="flex gap-1">
          <TopFactorsFootnote footnoteNumber={1} />
        </li>
        <li className="flex gap-1">
          <BaselineMetricFootnote
            industryMetric="87 monthly care hours"
            sourceUrl="https://acl.gov/ltc/basic-needs/who-will-provide-your-care"
            displayUrl="acl.gov"
            footNoteNumber={2}
          />
        </li>
      </ol>
    </>
  );
}

export function LtcAtAgeSlideOutContent() {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4 ">
        <div>
          <h1 className="flex-grow pb-2 text-lg font-semibold md:text-2xl">
            Starting age of long-term care
          </h1>
          <p className="text-sm md:text-base">
            The estimated age when long-term care (LTC)<sup>1</sup> begins.
          </p>
        </div>
        <div>
          <h2 className="text-lg font-semibold md:text-xl">
            Model accuracy<sup>2</sup>
          </h2>
          <p className="text-sm md:text-base">
            <BaselineMetricText percentIncrease={65} />
          </p>
        </div>
        <div>
          <h2 className="text-lg font-semibold md:text-xl">
            Top factors<sup className="font-normal">2</sup>
          </h2>
          <p className="text-sm md:text-base">
            BMI, sex, marital status, lung, memory, diabetes, mental health
          </p>
        </div>
      </div>
      {/* footer */}
      <ol className="flex flex-col border-t border-gray-200 py-6 italic text-gray-700">
        <li className="flex gap-1">
          <LtcDefinitionFootnote footnoteNumber={1} />
        </li>
        <li className="flex gap-1">
          <BaselineMetricFootnote
            industryMetric="80 years"
            sourceUrl="https://www.aaltci.org/news/long-term-care-insurance-news/newest-long-term-care-insurance-claims-paid-data-shared"
            displayUrl="aaltci.org"
            footNoteNumber={2}
          />
        </li>
        <li className="flex gap-1">
          <TopFactorsFootnote footnoteNumber={3} />
        </li>
      </ol>
    </>
  );
}

interface LtcCostsSlideOutContentProps {
  allPhaseInflatedProfessionalShareCost: number;
  clientHasStartedLtc: boolean;
  allPhaseProfessionalShareCost: number;
}

export function LtcCostsSlideOutContent(props: LtcCostsSlideOutContentProps) {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4 ">
        <div>
          <h1 className="flex-grow pb-2 text-lg font-semibold md:text-2xl">
            Cost of long-term care
          </h1>
          <p className="text-sm md:text-base">
            <LtcCostDisplay value={props.allPhaseInflatedProfessionalShareCost} /> is the estimated
            cost of long-term care (LTC) adjusted for health care inflation
            <sup>1</sup> up to the anticipated {`${props.clientHasStartedLtc ? 'end' : 'start'}`}{' '}
            year of care.
          </p>
        </div>

        <div>
          <h2 className="text-lg font-semibold md:text-xl">
            Model accuracy<sup className="font-normal">2</sup>
          </h2>
          <p className="text-sm md:text-base">
            <BaselineMetricText percentIncrease={65} />
          </p>
        </div>
        <div>
          <h2 className="text-lg font-semibold md:text-xl">
            Top factors<sup className="font-normal">3</sup>
          </h2>
          <p className="text-sm md:text-base">
            Memory, ADL/IADL baselines, care environment, city, age, mental health, arthritis,
            prescription drug use, BMI
          </p>
        </div>
      </div>
      {/* footer */}
      <ol className="flex flex-col border-t border-gray-200 py-6 italic text-gray-700">
        <li className="flex gap-1">
          <p>1. </p>
          <p className="text-sm md:text-base">
            Annual inflation rate of {annualHealthcareInflationRate * 100}%{' '}
            <a
              href="https://www.cms.gov/newsroom/press-releases/cms-office-actuary-releases-2022-2031-national-health-expenditure-projections"
              target="_blank"
              rel="noreferrer"
            >
              (source: cms.gov)
            </a>
          </p>
        </li>
        <li className="flex gap-1">
          <p className="text-sm md:text-base">
            <sup>2</sup>{' '}
          </p>
          <p className="text-sm md:text-base">
            This is an approximate comparison between Waterlily&apos;s model and a baseline model,
            based on 1 million simulations using industry-standard cost distributions for various
            care settings. For more detail visit our{' '}
            <a
              href={'https://www.waterlily.com/planning-for-yourself/disclosures-disclaimers'}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              disclaimers and methodology page
            </a>
            .
          </p>
        </li>
        <li className="flex gap-1">
          <TopFactorsFootnote footnoteNumber={3} />
        </li>
      </ol>
    </>
  );
}

interface FootNoteProps {
  footnoteNumber: number;
}

export function LtcDefinitionFootnote({ footnoteNumber }: FootNoteProps) {
  return (
    <>
      <p className="text-sm md:text-base">{footnoteNumber}.</p>
      <span className="text-sm md:text-base">
        LTC is defined by requiring assistance with at least two self-care activities (i.e.
        activites of daily living ADL) or an equivalent level of care. These activites include
        bathing, dressing, eating, transferrring (e.g., from bed to chair), toileting, and
        continence.
      </span>
    </>
  );
}

export function TopFactorsFootnote({ footnoteNumber }: FootNoteProps) {
  return (
    <>
      <p className="text-sm md:text-base">{footnoteNumber}.</p>
      <span className="text-sm md:text-base">
        The most important factors to the model on average, not necessarily for the individual
      </span>
    </>
  );
}

interface BaseMetricTextProps {
  percentIncrease: number;
}

export function BaselineMetricText({ percentIncrease }: BaseMetricTextProps) {
  return (
    <span className="text-sm md:text-base">
      {percentIncrease}% more accurate than the industry-standard approach.
    </span>
  );
}

interface BaselineMetricFootnoteProps {
  industryMetric: string;
  sourceUrl: string;
  displayUrl: string;
  footNoteNumber: number;
}

export function BaselineMetricFootnote({
  industryMetric,
  sourceUrl,
  displayUrl,
  footNoteNumber,
}: BaselineMetricFootnoteProps) {
  return (
    <>
      <p className="text-sm md:text-base">{footNoteNumber}. </p>
      <p className="text-sm md:text-base">
        The model is compared to a baseline model that predicts the industry-standard{' '}
        {industryMetric}
        <a
          href={sourceUrl}
          target="_blank"
          rel="noreferrer"
        >
          {' '}
          ({displayUrl})
        </a>
        , and the accuracy of both models is assessed on unseen data.
      </p>
    </>
  );
}

function PerformanceMetrics({ policyType }: { policyType?: FundingPolicyType }) {
  const client = useSelector(selectClient);
  const clientLtcPolicy = client?.multipleFundingSources?.ltcPolicy;
  const hasCareAssurance =
    clientLtcPolicy &&
    Object.values(clientLtcPolicy).some(
      policy => policy.policyType === FundingPolicyType.careScoutCareAssurance,
    );
  const inflationRate =
    client?.clientCalculationSettings?.healthCareInflationRate ?? annualHealthcareInflationRate;
  const isCareAssurance = policyType === FundingPolicyType.careScoutCareAssurance;

  return (
    <>
      <h2 className="flex flex-grow flex-col gap-3 pt-4 text-xl font-semibold">
        Performance Metrics
      </h2>
      <div className="space-y-4 text-sm md:text-base">
        <p>You can sort policies by two different performance metrics:</p>

        <ul className="ml-6 list-disc space-y-4">
          <li>
            <div className="space-y-3">
              <div className="font-semibold">Simple ROI (Return on Investment)</div>
              <p>
                This metric compares the total projected payout to the total investment, without
                considering the time over which payments are made.
              </p>
              <div className="flex justify-center py-4">
                <Latex>
                  {`$$ROI = \\frac{\\text{Total Payout} - \\text{Total Investment}}{\\text{Total Investment}}$$`}
                </Latex>
              </div>
            </div>
          </li>
          <li>
            <div className="space-y-3">
              <div className="font-semibold">CAGR (Compound Annual Growth Rate)</div>
              <p>
                CAGR provides a "smoothed out" annualized rate of return, showing what the annual
                growth rate would have been if it had occurred evenly over the entire period. This
                is useful for comparing a policy's performance to other types of long-term
                investments.
              </p>
              <div className="flex justify-center py-4">
                <Latex>
                  {`$$CAGR = \\left(\\frac{\\text{Total Payout}}{\\text{Present Value of Investment}}\\right)^{\\frac{1}{\\text{Years}}} - 1$$`}
                </Latex>
              </div>
              <ul className="ml-6 mt-3 list-disc">
                <li>
                  <span className="font-semibold">About the Discount Rate:</span> The CAGR
                  calculation includes a <span className="font-semibold">discount rate</span> that
                  you can set. This rate is used to calculate the{' '}
                  <span className="italic">Present Value of Investment</span> and is only relevant
                  for policies with future premium payments. A higher discount rate means future
                  premiums are considered less costly in today's dollars. For single-premium
                  (one-time) investments, the discount rate has no impact.
                </li>
              </ul>
            </div>
          </li>
          <li>
            <div className="space-y-3">
              <div className="font-semibold">IRR (Internal Rate of Return)</div>
              <p>
                IRR represents the annualized effective rate of return that makes the net present
                value of all cash flows equal to zero. Unlike CAGR, IRR accounts for the exact
                timing of all premium payments and benefit payouts throughout the policy lifetime,
                providing a more precise measure of investment performance when cash flows occur at
                irregular intervals.
              </p>
              <div className="flex justify-center py-4">
                <Latex>
                  {`$$\\sum_{i=1}^{n} \\frac{CF_i}{(1 + IRR)^{\\frac{\\text{days}_i}{365}}} = 0$$`}
                </Latex>
              </div>
              <p className="text-sm italic">
                Where CF<sub>i</sub> is the cash flow at time i (negative for investments, positive
                for returns), and days<sub>i</sub> is the number of days from the base year to cash
                flow i.
              </p>
              <ul className="ml-6 mt-3 list-disc space-y-2">
                <li>
                  <span className="font-semibold">XIRR Calculation:</span> IRR is calculated using
                  the XIRR method (Extended Internal Rate of Return), which uses the actual dates of
                  cash flows rather than assuming annual periods. This is particularly useful for
                  policies with monthly or annual premium payments spread over multiple years,
                  varying benefit payout schedules, or single premium payments with distant future
                  payouts.
                </li>
                <li>
                  <span className="font-semibold">Base Year for Policies:</span> For insurance
                  policies, the calculation uses the{' '}
                  <span className="font-semibold">policy premium start year</span> as the base year.
                  Premium payments are treated as negative cash flows (money invested), while
                  projected LTC benefits and any death benefits are treated as positive cash flows
                  (money returned).
                </li>
                <li>
                  <span className="font-semibold">Base Year for Self-Funding & Annuities:</span> For
                  self-funding and annuities, the{' '}
                  <span className="font-semibold">current year</span> is used as the base year since
                  contributions typically start in the present or near-present time.
                </li>
                <li>
                  <span className="font-semibold">Interpreting IRR:</span> A higher IRR indicates a
                  better return on investment. For example, an IRR of 5% means the policy
                  effectively grows your investment at 5% annually when accounting for all payment
                  and payout timing. You can compare this to other investment opportunities with
                  similar risk profiles.
                </li>
              </ul>
            </div>
          </li>
        </ul>

        <div className="mt-6 rounded-lg bg-gray-50 p-4 text-sm">
          <p className="font-medium text-gray-700">Note on Cost Savings Features</p>
          <p className="mt-1 text-gray-700">
            Some policies include cost-saving features, such as preferred care networks, that lower
            your out-of-pocket (OOP) costs for eligible home care services. These projected savings
            are shown by name in the metrics header of the long-term care financing plan and are
            incorporated into the Total ROI and CAGR calculations. In these calculations, the
            modeled OOP savings are added as part of the total value returned to the policyholder,
            which increases the policy&apos;s effective rate of return. However, these savings are
            not actual payments received from the policy. Instead, they represent a reduction in
            projected future home care expenses that would otherwise have been paid out of pocket.
            Because these savings are modeled as reduced expenses rather than income, they do not
            appear in the Total Payout column of the funding comparison table.
          </p>
          <br />
          <p className="font-medium text-gray-700">Understanding Savings Through Care Networks</p>
          <p className="mt-1 text-gray-700">
            If a policy includes access to a network of care providers offering preferred pricing,
            our model incorporates these potential savings. It is important to understand that these
            projections are <span className="font-medium">hypothetical demonstrations </span> based
            on several key assumptions:
          </p>
          <ul className="ml-6 mt-3 list-disc space-y-2 pb-4 text-gray-700">
            <li>
              <span className="font-medium">Network Utilization:</span> The model assumes you will
              find and choose to use network providers for your relevant out-of-pocket (OOP) home
              care service needs.
            </li>
            <li>
              <span className="font-medium">Discount Stability:</span> The model uses the current
              average discount rate provided by the carrier or network administrator. Actual
              discounts may vary significantly by location, provider, and time.
            </li>
            <li>
              <span className="font-medium">Cost Basis:</span> Savings are calculated against the
              projected median cost of care in your ZIP code, adjusted for inflation.
            </li>
          </ul>
          <span className="font-medium">Important Considerations for Network Benefits:</span>
          <ul className="ml-6 mt-3 list-disc space-y-2 text-gray-700">
            <li>
              <span className="font-medium">Non-Guaranteed:</span> Access to these networks is often
              a "non-policy benefit" and is typically{' '}
              <span className="font-medium">not guaranteed</span> by the insurance contract. The
              network, provider availability, and discount rates can be modified or discontinued at
              any time.
            </li>
            <li>
              <span className="font-medium">Variability: </span> The actual availability of
              providers and the level of savings in your specific area may change over time. The
              structure, vetting processes, and quality standards vary between different networks.
            </li>
          </ul>
        </div>
        {hasCareAssurance || isCareAssurance ? (
          <div className="space-y-4 p-4 text-sm md:text-base">
            <h3 className="text-lg font-semibold">
              Specific to Care Assurance: The CareScout Quality Network (CQN)
            </h3>
            <p>
              {isCareAssurance
                ? "You're currently looking at a CareScout Care Assurance product in the auto-quoter."
                : 'You currently have a CareScout Care Assurance product placed in your funding comparison table.'}{' '}
              This product includes access to the CareScout Quality Network (CQN), managed by
              CareScout, LLC. The CQN is a network of aging care providers evaluated by CareScout
              that have agreed to provide services at preferred rates.
            </p>

            <h4 className="pt-2 font-semibold">How We Model CQN Savings</h4>
            <p>
              Waterlily accounts for the potential reduction in OOP expenses for home care services
              when calculating the projected ROI and CAGR for the Care Assurance product. The
              modeled savings are included in the policy&apos;s Total Projected Payout.
            </p>
            <p>
              This projection is a <span className="font-semibold">hypothetical demonstration</span>{' '}
              (a "Non-policy Benefit") based on the following specific assumptions:
            </p>
            <ul className="ml-6 list-disc space-y-2">
              <li>
                <span className="font-semibold">Network Utilization:</span> The model assumes you
                will find and use providers within the CQN for your projected OOP home care service
                needs.
              </li>
              <li>
                <span className="font-semibold">Discount Rate:</span> The model assumes the costs
                for those providers will be{' '}
                <span className="font-semibold">
                  11%<sup>1</sup>
                </span>{' '}
                less than the projected median cost of home health care<sup>2</sup> in your ZIP
                code.
              </li>
              <li>
                <span className="font-semibold">Costs outside of the Network:</span> The model
                assumes that costs for home care services you would obtain without using the CQN
                would be at least equal to the projected median cost of home health care<sup>2</sup>{' '}
                in your ZIP code.
              </li>
            </ul>

            <h4 className="pt-2 font-semibold">About the Network Structure</h4>
            <ul className="ml-6 list-disc space-y-2">
              <li>
                <span className="font-semibold">Vetted Providers:</span> Providers in the CQN are
                evaluated against quality standards established by CareScout. This process includes
                a review of the provider's credentials (licensure, insurance, regulatory
                compliance), business practices (including staffing, training, and retention rates),
                financial health, and commitment to person-centered care practices.
              </li>
              <li>
                <span className="font-semibold">Scope of Savings:</span> The preferred pricing
                applies to costs covered by the Care Assurance policy (potentially extending the
                duration of benefits) as well as to any OOP expenses for home care services incurred
                by the policyholder. Note that preferred pricing may not apply to all charges
                associated with home care (for example, mileage fees).
              </li>
            </ul>

            <h4 className="pt-2 font-semibold">Limitations and Considerations for the CQN</h4>
            <p>
              The CQN is an actively growing network, but the projections rely on assumptions that
              may not reflect your actual experience:
            </p>
            <ul className="ml-6 list-disc space-y-2">
              <li>
                <span className="font-semibold">Non-Guaranteed Benefit:</span> Access to the CQN is
                not guaranteed by the insurance contract. It is a separate feature that can be
                modified or discontinued by CareScout at any time.
              </li>
              <li>
                <span className="font-semibold">Actual Savings Will Vary:</span> The{' '}
                <span className="font-semibold">11%</span>
                <sup>1</sup> figure is a national average. Any actual savings will vary; they may be
                higher or lower depending on your location, the specific providers used, and future
                rate negotiations for providers in the CQN. The discount rate may increase or
                decrease over time. Consider that CQN providers may not be the lowest cost providers
                in your area. Also consider that some providers, including CQN providers, may offer
                discounted rates to individuals seeking care without using the CQN.
              </li>
              <li>
                <span className="font-semibold">Network Evolution:</span> Coverage, provider
                availability, and discount rates in specific ZIP codes may shift over time (improve
                or decline) as the network evolves.
              </li>
            </ul>
            <p>
              We recommend considering how much of the policy's projected return relies on these
              non-guaranteed savings features when comparing options.
            </p>
            <p>
              <span className="font-semibold">Note:</span> To remove the projected CQN savings from
              your calculations, navigate to your care cost settings and enter a custom home care
              rate instead of using the default rate.
            </p>

            <h4 className="pt-2 font-semibold">Data Sources and Methodology Notes</h4>
            <p className="italic">
              <sup>1</sup> This savings percentage is based on information from CareScout, LLC
              indicating that, on average nationally, providers in the CareScout Quality Network
              offer preferred pricing that is 11% less than the median cost of home health care by
              metropolitan statistical area. This average is derived internally by comparing
              preferred CQN rates against the{' '}
              <a
                href="https://www.carescout.com/cost-of-care"
                target="_blank"
                rel="noreferrer"
                className="font-medium underline"
              >
                CareScout cost of care survey
              </a>
              . (Current as of 10/1/2025.)
            </p>
            <p className="italic">
              <span className="pt-2 font-semibold"> Note on Cost Data: </span>
              The CareScout survey used to derive the 11% average is a different source from the
              baseline costs used generally within the Waterlily platform (which are sourced from
              the{' '}
              <a
                href="https://cdn.ltcfeds.gov/planning-tools/downloads/Cost-of-Care-Survey.pdf"
                target="_blank"
                rel="noreferrer"
                className="font-medium underline"
              >
                2021 Cost of Care Survey
              </a>{' '}
              published by the Federal Long Term Care Insurance Program
              <a
                href="https://ltcfeds.gov/"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                (LTCFEDS.gov)
              </a>
              . Waterlily models the 11% savings rate against Waterlily's estimated ZIP code costs.
            </p>
            <p className="italic">
              <sup>2</sup> The projected median cost of care is based on median cost of care
              information published by CareScout Holdings, Inc., assuming a {inflationRate * 100}%
              annual inflation rate.
            </p>
          </div>
        ) : null}
      </div>
    </>
  );
}

function ImportantLimitations() {
  return (
    <>
      <h2 className="flex flex-grow flex-col gap-3 pb-2 pt-4 text-xl font-semibold">
        Important Limitations
      </h2>
      <ul className="ml-6 list-disc">
        <li>
          <span className="font-semibold">Model Scope & Nuance:</span>
          While our models are highly detailed, they may not capture every unique provision, rider
          nuance,or claim restriction within a given policy. These projections are intended to
          provide a robust framework for <span className="font-semibold">comparing options</span>,
          not to serve as a guarantee of the exact payout down to the dollar. We regularly enhance
          our calculation engine to incorporate more of these policy-specific details.
        </li>
        <li>
          <span className="font-semibold">Interpolated Estimates:</span>
          The initial quotes and benefits you see are based on pre-calculated data. If you adjust
          the premium amount, the corresponding benefits are
          <span className="font-semibold"> interpolated estimates</span>, not live quotes from the
          carrier. While this method is highly accurate (typically varying by less than 1%), it is
          still an approximation. An exact, carrier-confirmed quote will be generated and presented
          for your final review before you apply.
        </li>
        <li>
          <span className="font-semibold">Projection Accuracy:</span>
          All performance figures are forward-looking estimates and are only as accurate as the
          underlying assumptions in your LTC plan. Your actual LTC needs and costs may vary
          significantly, which would change the policy's real-world return.
        </li>
      </ul>
    </>
  );
}

export function CostDataMethodologySlideOutContent() {
  return (
    <>
      <div className="flex flex-grow flex-col gap-4">
        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Data Source</h2>
          <p className="text-sm md:text-base">
            Our baseline costs are sourced from the{' '}
            <a
              href="https://cdn.ltcfeds.gov/planning-tools/downloads/Cost-of-Care-Survey.pdf"
              target="_blank"
              rel="noreferrer"
              className="font-medium underline"
            >
              2021 Cost of Care Survey
            </a>{' '}
            published by the Federal Long Term Care Insurance Program{' '}
            <a
              href="https://ltcfeds.gov/"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              (LTCFEDS.gov)
            </a>
            . This survey provides detailed cost data for various care settings across the United
            States. We map the ZIP code you designated in your intake form as your planned location
            for future care to the corresponding geographical area in this survey.
          </p>
        </div>

        <div>
          <h2 className="pb-2 text-lg font-semibold md:text-xl">Inflation to Present Day</h2>
          <p className="pb-3 text-sm md:text-base">
            To account for rising healthcare expenses, we inflate the 2021 costs to the current year
            using a precise, year-by-year compounding method. This method uses the official annual
            National Health Expenditure (NHE) growth rates published by the U.S. Centers for
            Medicare & Medicaid Services (CMS).
          </p>

          <p className="pb-2 text-sm md:text-base">The calculation is performed as follows:</p>
          <ul className="mb-3 ml-6 list-disc space-y-2 text-sm md:text-base">
            <li>
              To project costs for 2022, we inflate the 2021 base by 2.7%{' '}
              <a
                href="https://www.cms.gov/newsroom/press-releases/national-health-spending-grew-slightly-2021"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                (Source: CMS 2021 NHE Press Release)
              </a>
              .
            </li>
            <li>
              To project costs for 2023, we inflate the resulting 2022 cost by 4.6%{' '}
              <a
                href="https://www.cms.gov/newsroom/fact-sheets/national-health-expenditures-2022-highlights"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                (Source: CMS 2022 NHE Highlights)
              </a>
              .
            </li>
            <li>
              To project costs for 2024, we inflate the 2023 cost by 7.5%{' '}
              <a
                href="https://www.cms.gov/files/document/highlights.pdf"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                (Source: CMS 2023 NHE Highlights)
              </a>
              .
            </li>
            <li>
              Finally, to arrive at the current 2025 cost, we inflate the 2024 cost by 8.2%{' '}
              <a
                href="https://www.cms.gov/files/document/nhe-projections-forecast-summary.pdf"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                (Source: CMS NHE Projections)
              </a>
              .
            </li>
          </ul>

          <p className="text-sm md:text-base">
            This precise historical inflation adjustment is compounded annually and is "baked-in" to
            your plan's starting costs to bring the 2021 data to present day values.
          </p>
        </div>
      </div>
    </>
  );
}

export function CareAssuranceSavingsSlideOutContent() {
  const client = useSelector(selectClient);
  const savingsAmount =
    client?.allPhaseCosts?.allPhaseInflatedProfessionalShareCostDiscountAmount ?? 0;
  const discountPercentage =
    client?.clientLocalCareEnvironmentCosts?.[CareEnvironment.home]?.discount?.percent ?? 11;

  return (
    <>
      <div className="flex flex-grow flex-col gap-4 pb-6">
        <div className="space-y-4 text-sm md:text-base">
          <h1 className="flex flex-grow flex-col gap-3 pb-2 text-lg font-semibold md:text-2xl">
            Understanding Care Assurance Savings
          </h1>
          <p>
            The savings projected here relate to the CareScout Quality Network (CQN), a feature
            currently offered with the Care Assurance policy. The CQN, managed by CareScout, LLC (an
            affiliate of CareScout Insurance Company), is a network of aging care providers that
            have been evaluated by CareScout and have agreed to provide services at preferred rates
            (discounts).
          </p>

          <h2 className="pb-2 text-lg font-semibold md:text-xl">How We Calculate These Savings</h2>
          <p>
            The amount shown ({formatCurrency(savingsAmount)}) represents a{' '}
            <span className="font-semibold">hypothetical demonstration</span> (a "Non-policy
            Benefit") of how utilizing CQN providers could lower your out-of-pocket (OOP) costs for
            home care services. Waterlily includes these potential savings in the policy's Total
            Projected ROI and CAGR.
          </p>
          <p>Our calculation relies on the following assumptions:</p>
          <ol className="ml-6 list-decimal space-y-2">
            <li>
              <span className="font-semibold">Network Utilization:</span> The model assumes you will
              find and use providers within the CQN for your projected OOP home care service needs.
            </li>
            <li>
              <span className="font-semibold">Discount Rate:</span> The model assumes the costs for
              those providers will be{' '}
              <span className="font-semibold">
                {discountPercentage}%<sup>1</sup>
              </span>{' '}
              less than the projected median cost of home health care<sup>2</sup> in your ZIP code.
            </li>
            <li>
              <span className="font-semibold">Costs outside of the Network:</span> The model assumes
              that costs for home care services you would obtain without using the CQN would be at
              least equal to the projected median cost of home health care<sup>2</sup> in your ZIP
              code.
            </li>
          </ol>

          <h2 className="pb-2 pt-4 text-lg font-semibold md:text-xl">
            Network Structure and Benefits
          </h2>
          <p>
            The CQN feature is designed to connect policyholders with vetted providers at preferred
            rates.
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              <span className="font-semibold">Vetted Providers:</span> Providers in the CQN are
              evaluated against quality standards established by CareScout. This includes a review
              of credentials (licensure, insurance), business practices (staffing, training,
              retention rates), financial health, and commitment to person-centered care practices.
            </li>
            <li>
              <span className="font-semibold">Scope of Savings:</span> The preferred pricing applies
              to costs covered by the Care Assurance policy (potentially extending the duration of
              benefits) as well as to any OOP expenses for home care services incurred by the
              policyholder. Note that preferred pricing may not apply to all charges associated with
              home care (for example, mileage fees).
            </li>
          </ul>

          <h2 className="pb-2 pt-4 text-lg font-semibold md:text-xl">
            Limitations and Considerations
          </h2>
          <p>
            The CQN is an actively growing network, but the projections rely on assumptions that may
            not reflect your actual experience:
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              <span className="font-semibold">Non-Guaranteed Benefit:</span> Access to the CQN is
              not guaranteed by the insurance contract. It is a separate feature that can be
              modified or discontinued by CareScout at any time.
            </li>
            <li>
              <span className="font-semibold">Actual Savings Will Vary:</span> The{' '}
              {discountPercentage}%<sup>1</sup> figure is a national average. Any actual savings
              will vary; they may be higher or lower depending on your location and the specific
              providers used, and future rate negotiations for providers in the CQN. The discount
              rate may increase or decrease over time. Consider that CQN providers may not be the
              lowest cost providers in your area. Also consider that some providers, including CQN
              providers, may offer discounted rates to individuals seeking care without using the
              CQN.
            </li>
            <li>
              <span className="font-semibold">Network Evolution:</span> Coverage, provider
              availability, and discount rates in specific ZIP codes may shift over time (improve or
              decline) as the network evolves.
            </li>
          </ul>
          <p>
            <span className="font-semibold">Note:</span> To remove the projected CQN savings from
            your calculations, navigate to your care cost settings and enter a custom home care rate
            instead of using the default rate.
          </p>

          <h2 className="pb-2 pt-4 text-lg font-semibold md:text-xl">
            Data Sources and Methodology Notes
          </h2>
          <p className="italic text-gray-700">
            <sup>1</sup> This savings percentage is based on information from CareScout, LLC
            indicating that, on average nationally, providers in the CareScout Quality Network offer
            preferred pricing that is {discountPercentage}% less than the median cost of home health
            care by metropolitan statistical area. This average is derived internally by comparing
            preferred CQN rates against the{' '}
            <a
              href="https://www.carescout.com/cost-of-care"
              target="_blank"
              rel="noreferrer"
              className="font-medium underline"
            >
              CareScout cost of care survey
            </a>
            . (Current as of 10/1/2025.)
          </p>
          <p className="pt-2 italic text-gray-700">
            <span className="font-semibold">Note on Cost Data:</span> The CareScout survey used to
            derive the {discountPercentage}% average is a different source from the baseline costs
            used generally within the Waterlily platform (which are sourced from the{' '}
            <a
              href="https://cdn.ltcfeds.gov/planning-tools/downloads/Cost-of-Care-Survey.pdf"
              target="_blank"
              rel="noreferrer"
              className="font-medium underline"
            >
              2021 Cost of Care Survey
            </a>{' '}
            published by the Federal Long Term Care Insurance Program{' '}
            <a
              href="https://ltcfeds.gov/"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              (LTCFEDS.gov)
            </a>
            . Waterlily models the {discountPercentage}% discount against Waterlily's estimated ZIP
            code costs.
          </p>
          <p className="pt-2 italic text-gray-700">
            <sup>2</sup> The projected median cost of care is based on median cost of care
            information published by CareScout Holdings, Inc., assuming an{' '}
            {(client?.clientCalculationSettings?.healthCareInflationRate ??
              annualHealthcareInflationRate) * 100}
            % annual inflation rate.
          </p>
        </div>
      </div>
    </>
  );
}

export function ROISlideOutContent({ policyType }: { policyType?: FundingPolicyType }) {
  return (
    <>
      <div className="-mt-10 flex flex-grow flex-col gap-4 pb-6">
        <div className="mt-20 space-y-4 text-sm md:text-base">
          <h1 className="flex flex-grow flex-col gap-3 pb-2 text-lg font-semibold md:text-2xl">
            How We Project Policy Performance
          </h1>
          <p>
            The Return on Investment (ROI), Compound Annual Growth Rate (CAGR), and other metrics
            shown are <span className="font-semibold">illustrative projections</span>, not
            guarantees. They are designed to help you compare policies based on a consistent set of
            assumptions.
          </p>

          <p>
            Our calculation models how each policy might perform against your personalized Long-Term
            Care (LTC) plan. This plan is based on either Waterlily's AI-driven predictions or the
            specific assumptions you've entered regarding your future care needs, timing, and costs.
          </p>

          <p>Here's a simplified breakdown of our process:</p>

          <ol className="ml-6 list-decimal space-y-3">
            <li>
              <span className="font-semibold">Your Investment:</span> We calculate the total
              premiums you would pay into the policy up to the projected start of your care.
            </li>
            <li>
              <span className="font-semibold">Your Potential Payout:</span> We simulate your LTC
              event phase-by-phase, applying each policy's specific rules. The total payout
              includes:
              <ul className="ml-6 mt-2 list-disc space-y-1">
                <li>
                  <span className="font-semibold">LTC Benefits:</span> Payments for care, accounting
                  for benefit triggers (like ADL limitations), waiting periods, inflation
                  protection, and daily or monthly maximums.
                </li>
                <li>
                  <span className="font-semibold">Residual Value:</span> Any remaining death benefit
                  payable after LTC claims, plus other potential payouts like indemnity surpluses or
                  savings through care networks.
                </li>
                <li>
                  <span className="font-semibold">Cost Savings:</span> If your policy has a cost
                  savings feature, this will be included in the payout.
                </li>
              </ul>
            </li>
            <li>
              <span className="font-semibold">Modeling Joint Policies:</span> For joint policies,
              our model assesses the potential payout for{' '}
              <span className="font-semibold">both</span> individuals listed. It uses the distinct
              LTC plan (either predicted or customized) for each person and simulates a scenario
              where both individuals go on claim according to their respective timelines. The total
              projected payout reflects the combined benefits used by both policyholders from the
              shared benefit pool.
            </li>
          </ol>
        </div>
        <PerformanceMetrics policyType={policyType} />
        <ImportantLimitations />
      </div>
    </>
  );
}
